import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';

import {
  PrintOptionPriceDetailsDto,
  PrintPositionPriceDetailsDto,
  ProductService,
} from '@/api-sdk';
import { iconCaret } from '@/components/icons';
import {
  BREAKPOINT_EXTRA_LARGE_PX,
  BREAKPOINT_LARGE_PX,
  BREAKPOINT_MEDIUM_PX,
} from '@/settings';
import { baseStyles, typographyStyles } from '@/styles';
import { LayoutType, RowData } from '@/types';
import {
  i18nInit,
  makeColumnHeadingsArrayFromQuantityRanges,
  makeColumnRowsArrayFromScales,
  setBaseUrl,
} from '@/utils';
import '../loader/fg-loader';
import './fg-table';

@customElement('fg-print-price-tables')
@localized()
export class FgPrintPriceTables extends LitElement {
  @property({ type: String })
  product = '';

  @property({ type: String })
  baseUrl = '';

  @property({ type: String })
  locale = 'nl_NL';

  /**
   * Debtor is the loggedin reseller ID from CE
   *
   * @property {string} debtor
   */
  @property({ type: String })
  debtor: string | undefined = undefined;

  /**
   * Country is the country from the loggedin reseller from CE
   *
   * @property {string} country
   */
  @property({ type: String })
  country: string | undefined = undefined;

  @property({ type: String })
  layout: LayoutType = 'wholesale';

  @property({ type: Array })
  positions: PrintPositionPriceDetailsDto[] = [];

  @state()
  quantityRanges: string[] = [];

  @state()
  scales: RowData = [];

  @property({ type: Boolean })
  hasPrintingHeadingImages = false;

  @state()
  isLoading = true;

  @state()
  positionTable: any[] = [];

  private collapsiblesRef: Ref<HTMLElement>[] = [];

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  async firstUpdated() {
    this.getPriceScalePerPrintOptionTable();
  }

  async getPriceScalePerPrintOptionTable() {
    try {
      const priceScalePerPrintOptionInfo =
        await ProductService.getShowProductPrintOptionPriceScales({
          locale: this.locale,
          code: this.product,
          debtor: this.debtor,
          country: this.country,
        });

      if (priceScalePerPrintOptionInfo) {
        this.positions = priceScalePerPrintOptionInfo;
        this.checkPrintingHeadingImages();
        this.isLoading = false;
      }
    } catch {
      this.isLoading = false;
    }
  }

  checkPrintingHeadingImages() {
    for (const position of this.positions) {
      this.hasPrintingHeadingImages = !!position.printOptions.find(
        printOption => printOption.image !== '',
      );
    }
  }

  toggleCollapsible(
    event: Event,
    positionArray1: number,
    positionArray2: number,
  ) {
    const element = event.currentTarget as HTMLElement;
    const collapsibleContainer = element?.parentElement;
    collapsibleContainer?.classList.toggle('collapsible-open');

    if (
      this.positionTable[positionArray1][positionArray2].value.style.maxHeight
    ) {
      this.positionTable[positionArray1][positionArray2].value.style.maxHeight =
        null;
    } else {
      this.positionTable[positionArray1][positionArray2].value.style.maxHeight =
        this.positionTable[positionArray1][positionArray2].value.scrollHeight +
        'px';
    }
  }

  renderPrintOptionItems(printOption: PrintOptionPriceDetailsDto) {
    const items: {
      label: string;
      value: string | number | null | undefined;
    }[] = [
      {
        label: 'labels.printSetupCost',
        value: printOption?.setupPrice?.formatted,
      },
      {
        label: 'labels.printMaxPrintSize',
        value: printOption?.dimensions,
      },
      {
        label: 'labels.printPrintingCode',
        value: printOption?.methodCode,
      },
      {
        label: 'labels.printOptionSurface',
        value: printOption?.area,
      },
      {
        label: 'labels.printHandlingCost',
        value: printOption?.handlingPrice?.formatted,
      },
      {
        label: 'labels.printMaxDiameter',
        value: printOption?.diameter,
      },
      {
        label: 'labels.printMaxColors',
        value: printOption?.maximumColorCount ?? '-',
      },
    ];

    return html`
      <div class="printing-info">
        ${items.map(item =>
          item?.value
            ? html`
                <div class="printing-info-item">
                  <div class="printing-info-label">${t(item.label)}:</div>
                  <div class="printing-info-value">${item.value}</div>
                </div>
              `
            : nothing,
        )}
      </div>
    `;
  }

  render() {
    if (this.isLoading) {
      return html`
        <fg-loader type="configuration"></fg-loader>
      `;
    }

    return html`
      <header>
        <h2>${t('labels.printOptions')}</h2>
      </header>
      ${this.positions.map((position, positionIndex) => {
        this.positionTable.push([]);
        return html`
          <header>
            <h4>${position.position}</h4>
          </header>
          <div class="position-container">
            ${position.printOptions.map((printOption, printOptionIndex) => {
              this.collapsiblesRef[printOptionIndex] = createRef();
              this.positionTable[positionIndex].push(
                this.collapsiblesRef[printOptionIndex],
              );
              const wrapperRef = ref(
                this.positionTable[positionIndex][printOptionIndex],
              );

              const printOptionHeadings =
                makeColumnHeadingsArrayFromQuantityRanges(
                  printOption.priceScaleTableDto.quantityRanges,
                  true,
                );

              return html`
                <div class="collapsible-container">
                  <div
                    class="printing-heading-container"
                    @click=${(event: Event) =>
                      this.toggleCollapsible(
                        event,
                        positionIndex,
                        printOptionIndex,
                      )}
                  >
                    <div class="printing-heading-part">
                      ${this.hasPrintingHeadingImages
                        ? printOption.image
                          ? html`
                              <div class="printing-img-container">
                                <img
                                  src="${printOption.image}"
                                  class="printing-img"
                                  alt="${printOption.technique}"
                                />
                              </div>
                            `
                          : html`
                              <div class="printing-img-container"></div>
                            `
                        : nothing}
                      <div>
                        <h4 class="printing-heading-title">
                          ${printOption.technique} (${printOption.methodCode})
                        </h4>
                        <div class="printing-dimensions-mobile">
                          ${printOption.dimensions}
                        </div>
                      </div>
                    </div>
                    <div class="printing-heading-part">
                      <div class="printing-dimensions-desktop">
                        ${printOption.dimensions}
                      </div>
                      <span class="collapsible-caret">${iconCaret}</span>
                    </div>
                  </div>
                  <div ${wrapperRef} class="printing-collapsible">
                    ${this.renderPrintOptionItems(printOption)}
                    <div class="printing-table-container">
                      <fg-table
                        forceShowScrollbar
                        .columnHeadings=${printOptionHeadings()}
                        .columnRows=${makeColumnRowsArrayFromScales(
                          printOption.priceScaleTableDto.scales,
                        )}
                      ></fg-table>
                    </div>
                    <div class="printing-costs-disclaimer">
                      ${t('content.printingCostsDisclaimer')}
                    </div>
                  </div>
                </div>
              `;
            })}
          </div>
        `;
      })}
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    css`
      :host([part='wholesale']) header h2 {
        margin-bottom: 0.5rem;
      }
      :host([part='wholesale']) header h4 {
        margin-bottom: 1rem;
      }
      .position-container {
        margin-bottom: 4rem;
      }
      .collapsible-container {
        margin-bottom: 0.25rem;
      }
      .collapsible-container.collapsible-open {
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
      }
      .collapsible-container:not(.collapsible-open)
        .printing-heading-container {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
      }
      .printing-collapsible {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.2s ease-out;
      }
      .printing-heading-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        padding: 1rem;
      }
      .printing-heading-part {
        display: flex;
        align-items: center;
      }
      .printing-img-container {
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        aspect-ratio: 1 / 1;
        position: relative;
        overflow: hidden;
        width: 48px;
        margin-right: 1rem;
      }
      .printing-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      :host([part='wholesale']) .printing-heading-title {
        font-size: 1rem;
      }
      .printing-dimensions-mobile {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        font-weight: 400;
      }
      .printing-dimensions-desktop {
        display: none;
      }
      .collapsible-container:not(.collapsible-open) .collapsible-caret {
        transform: rotate(0deg);
        transition: transform 0.2s ease-out;
      }
      .collapsible-container.collapsible-open .collapsible-caret {
        transform: rotate(180deg);
        transition: transform 0.2s ease-out;
      }
      .printing-info .printing-info-item:nth-child(odd) {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
      }
      .printing-info {
        padding: 0 0.5rem;
        margin-bottom: 1rem;
      }
      .printing-info-item {
        display: flex;
      }
      .printing-info-label,
      .printing-info-value {
        padding: 1rem 0.5rem;
        width: 50%;
        font-size: 1rem;
      }
      .printing-info-label {
        font-weight: 400;
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
      }
      .printing-info-value {
        font-weight: 700;
        color: var(--fg-primary, var(--fg-fallback-primary));
      }
      .printing-info-value {
        text-align: right;
      }
      .printing-table-container {
        padding: 0 1rem;
        margin-bottom: 1rem;
      }
      .printing-table-container fg-table {
        display: flex;
        justify-content: center;
        width: 100%;
      }
      .printing-costs-disclaimer {
        padding: 0 1rem 1rem;
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        font-weight: 400;
      }
      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        :host([part='wholesale']) header h2 {
          margin-bottom: 1.5rem;
        }
        :host([part='wholesale']) header h4 {
          margin-bottom: 1rem;
          font-size: 1.5rem;
        }
        .position-container {
          margin-bottom: 2.5rem;
        }
        .printing-heading-container {
          padding: 1rem 2rem 1rem 1rem;
        }
        .collapsible-container.collapsible-open .printing-heading-container {
          margin-bottom: 2rem;
        }
        .printing-img-container {
          width: 70px;
          margin-right: 1.5rem;
        }
        :host([part='wholesale']) .printing-heading-title {
          font-size: 1.25rem;
        }
        .printing-dimensions-mobile {
          display: none;
        }
        .printing-dimensions-desktop {
          display: block;
          font-family: var(
            --fg-font-family--brand,
            var(--fg-fallback-font-family--brand)
          );
          font-size: 1.25rem;
          font-weight: 600;
          margin-right: 1rem;
        }
        .printing-info {
          display: flex;
          flex-wrap: wrap;
          padding: 0 1rem;
          margin: 0 0 2rem;
        }
        .printing-info-item {
          width: 50%;
        }
        .printing-info .printing-info-item:nth-child(odd) {
          background-color: inherit;
          border-radius: 0;
        }
        .printing-info-label,
        .printing-info-value {
          padding: 0;
        }
        .printing-info-label {
          width: 60%;
        }
        .printing-info-value {
          width: 40%;
          text-align: left;
          padding: 0 1rem;
        }
      }
      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        .printing-table-container {
          display: flex;
          justify-content: center;
        }
      }
      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .printing-info {
          margin: 0 94px 2rem 94px;
        }
      }
    `,
  ];
}
